/**
 * OpenAI Sora 2 官方视频接口
 *
 * POST /v1/videos
 * GET  /v1/videos/{id}
 * GET  /v1/videos/{id}/content
 */
import axios from 'axios'
import { extractApiErrorMessage } from '../../extract-api-error-message'

var POLL_INTERVAL = 15000
var POLL_TIMEOUT = 15 * 60 * 1000

var CREATE_PATH = '/v1/videos'
var VIDEOS_PATH = '/v1/videos/'

function getSora2Config() {
  return {
    baseURL: (process.env.VUE_APP_OFFICIAL_SORA2_BASE_URL || 'https://api.openai.com').replace(/\/$/, ''),
    apiKey: (process.env.VUE_APP_OFFICIAL_SORA2_API_KEY || '').trim(),
    model: 'sora-2-pro'
  }
}

function createClient() {
  var cfg = getSora2Config()
  if (!cfg.baseURL) throw new Error('未配置 VUE_APP_OFFICIAL_SORA2_BASE_URL')
  if (!cfg.apiKey) throw new Error('未配置 VUE_APP_OFFICIAL_SORA2_API_KEY')
  var instance = axios.create({
    baseURL: cfg.baseURL,
    timeout: 120000,
    headers: {
      Authorization: 'Bearer ' + cfg.apiKey
    }
  })
  instance.interceptors.response.use(
    function (res) { return res.data },
    function (err) {
      var respData = err.response && err.response.data
      var msg =
        extractApiErrorMessage(respData) ||
        (respData && respData.error && respData.error.message) ||
        (respData && (respData.message || respData.messge)) ||
        err.message ||
        '请求失败'
      return Promise.reject(new Error(msg))
    }
  )
  return instance
}

function normalizeSeconds(seconds) {
  var s = parseInt(seconds, 10)
  if (s !== 4 && s !== 8 && s !== 12) s = 8
  return String(s)
}

function mapSize(aspectRatio) {
  var ar = String(aspectRatio || '').trim()
  return ar === '9:16' ? '1024x1792' : '1792x1024'
}

function buildFormData(params) {
  var formData = new FormData()
  formData.append('prompt', (params.prompt || '').trim().slice(0, 4000))
  formData.append('model', 'sora-2-pro')
  formData.append('size', mapSize(params.aspectRatio))
  formData.append('seconds', normalizeSeconds(params.videoDuration))
  if (params.referenceImage) {
    formData.append('input_reference[image_url]', String(params.referenceImage))
  }
  return formData
}

function downloadContentBlob(client, videoId) {
  var cfg = getSora2Config()
  return axios.get(cfg.baseURL + VIDEOS_PATH + encodeURIComponent(videoId) + '/content', {
    responseType: 'blob',
    timeout: 120000,
    headers: {
      Authorization: 'Bearer ' + cfg.apiKey
    }
  }).then(function (res) {
    return res.data
  })
}

function downloadVideoBlob(blob) {
  // Sora2 已经通过 responseType: 'blob' 拿到 blob，这里只是包装一下
  console.log('[Sora2] video blob ready', blob.size, 'bytes')
  return Promise.resolve(blob)
}

export function submitVolcSora2Task(params) {
  var client = createClient()
  var prompt = (params.prompt || '').trim()
  if (!prompt) return Promise.reject(new Error('prompt 必填'))
  var formData = buildFormData(params)
  return client.post(CREATE_PATH, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  }).then(function (data) {
    var id = data && data.id ? String(data.id) : ''
    if (!id) return Promise.reject(new Error('官方接口未返回视频 id'))
    return { taskId: id, videoMode: params.videoMode || 'text' }
  })
}

function parseTaskStatus(data) {
  var statusRaw = data && data.status ? String(data.status).toLowerCase() : ''
  if (statusRaw === 'queued' || statusRaw === 'in_progress') {
    return { status: 'processing', error: null }
  }
  if (statusRaw === 'failed') {
    var errorMsg = data && data.error && data.error.message ? String(data.error.message) : '生成失败'
    return { status: 'failed', error: errorMsg }
  }
  if (statusRaw === 'completed') {
    return { status: 'completed', error: null }
  }
  return { status: 'failed', error: '任务状态未知：' + statusRaw }
}

export function pollVolcSora2Task(taskId, callback, interval) {
  var pollInterval = interval || POLL_INTERVAL
  var deadline = Date.now() + POLL_TIMEOUT
  var pollTimer = null
  var stopped = false
  var resolvingSuccess = false
  var client = createClient()
  var path = VIDEOS_PATH + encodeURIComponent(taskId)

  function stop() {
    stopped = true
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
  }

  function poll() {
    if (stopped) return
    client
      .get(path)
      .then(function (data) {
        if (stopped) return
        var parsed = parseTaskStatus(data)
        if (parsed.status === 'completed') {
          if (resolvingSuccess) return
          resolvingSuccess = true
          downloadContentBlob(client, taskId)
            .then(function (blob) {
              return downloadVideoBlob(blob)
            })
            .then(function (blob) {
              if (typeof callback === 'function') {
                callback(null, { status: 'completed', url: '', videoBlob: blob, error: null })
              }
              stop()
            })
            .catch(function (err) {
              if (typeof callback === 'function') callback(err, null)
              stop()
            })
          return
        }

        if (parsed.status === 'failed') {
          if (typeof callback === 'function') callback(null, { status: 'failed', url: null, error: parsed.error || '生成失败' })
          stop()
          return
        }

        if (typeof callback === 'function') callback(null, { status: 'processing', url: null, error: null })
        if (Date.now() > deadline) {
          if (typeof callback === 'function') callback(new Error('轮询超时'), null)
          stop()
        } else {
          pollTimer = setTimeout(poll, pollInterval)
        }
      })
      .catch(function (err) {
        if (stopped) return
        if (typeof callback === 'function') callback(err, null)
        stop()
      })
  }

  poll()
  return { stop: stop }
}
